'use client'

import { collectPayment, getLocalRegister } from '@/lib/actions'
import { useCart, useCheckout, useModals, useSelectedCustomer } from '@/lib/hooks'
import { formatPrice } from '@/lib/utils'
import { parseAsString, useQueryState } from 'nuqs'
import type React from 'react'
import { use } from 'react'
import { useFormStatus } from 'react-dom'
import { CheckCircleBold, CloseCircleBold } from 'solar-icons'
import { toast } from 'sonner'

import { RefContext, type Refs } from '@/components/providers/ref-provider'
import {
  Button,
  Dialog,
  DialogContent,
  KeyChip,
  LoadingSpinner,
} from '@/components/ui'

interface ConfirmPaymentProps extends React.ComponentPropsWithoutRef<'div'> {
  open?: boolean
}

export const ConfirmPaymentModal = ({ open }: ConfirmPaymentProps) => {
  const { amountInputRef } = use(RefContext) as Refs
  const [cartId] = useQueryState('cartId', parseAsString.withDefault(''))
  const { customer } = useSelectedCustomer()
  const { getAndUpdateCart } = useCart()

  const { closeModal } = useModals()
  const {
    getPayments,
    currentAmountToCollect,
    payment_method_type,
    payment_card_last_digits,
    payment_method,
    balance_to_paid,
    futurePaymentDate,
    custom_reference,
  } = useCheckout()

  const handlePaymentCollection = async () => {
    const register: any = await getLocalRegister()

    const amountToCollect = currentAmountToCollect

    try {
      const res = await collectPayment({
        code: payment_method_type,
        cart_id: cartId,
        reference_id: Number(custom_reference) ?? null,
        order_id: null,
        status: 'paid', //paid , refund or void(for delete payment)
        currency: 'LKR',
        amount: currentAmountToCollect,
        bin: null, // first 6 digits of card
        last_digits: payment_card_last_digits,
        transaction_id: null,
        payment_method_id: payment_method?.id,
        pos_register_id: register?.id,
        future_payment_date: futurePaymentDate,
        custom_reference: custom_reference
          ? `${custom_reference} - ${payment_method?.name}`
          : `${payment_method?.name} - ${payment_method?.group}`,
        customer_id: customer?.id ?? null,
        payment_method: payment_method,
      })

      if (res?.error) {
        toast.error(res?.error)
        return
      }

      toast.success(
        `${formatPrice(currentAmountToCollect, 2, true)} collected successfully.`,
      )

      if (amountInputRef?.current) {
        // Add a small delay to ensure the input is properly updated before focusing
        setTimeout(() => {
          amountInputRef?.current?.focus()
        }, 100)

        if (payment_method?.group === 'cash') {
          amountInputRef.current.value = ''

          const event = new Event('input', { bubbles: true })
          amountInputRef.current.dispatchEvent(event)
        } else {
          // Use integer arithmetic to avoid floating point precision issues
          const balanceCents = Math.round(balance_to_paid * 100)
          const collectedCents = Math.round(amountToCollect * 100)
          const remainingCents = balanceCents - collectedCents
          const remainingBalance = (remainingCents / 100).toFixed(2)
          amountInputRef.current.value = remainingBalance
          // Trigger input event to ensure React state is updated
          const event = new Event('input', { bubbles: true })
          amountInputRef.current.dispatchEvent(event)
        }
      }

      getAndUpdateCart(cartId)
      getPayments({ cartId })
      closeModal('CONFIRM_PAYMENT')
    } catch (error: any) {
      if (typeof error === 'string') {
        toast.error(error)
        return
      }

      toast.error(error?.message)
      return
    }
  }

  return (
    <Dialog open={open}>
      <DialogContent
        closeOnOverlay
        onClose={() => closeModal('CONFIRM_PAYMENT')}
        size="lg"
        className="min-h-[410px] !pt-16"
      >
        <div className="flex flex-col items-center gap-4">
          <h2 className="text-base font-medium">Collecting Payment</h2>
          <h1 className="font-mono text-3xl font-extrabold lg:text-5xl">
            {formatPrice(currentAmountToCollect, 2, true)}
          </h1>
        </div>

        <div className="flex flex-col py-10">
          <div className="flex justify-center gap-2 text-sm">
            <LoadingSpinner
              strokeWidth={1.5}
              className="size-5 animate-spin duration-5000"
            />
            <span>Awaiting Confirmation...</span>
          </div>
        </div>

        <form
          action={handlePaymentCollection}
          className="mx-auto flex w-full max-w-[400px] flex-col items-center gap-2"
        >
          <ActionButtons
            onClickCancel={() => {
              closeModal('CONFIRM_PAYMENT')
            }}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface ActionButtonsProps extends React.ComponentPropsWithoutRef<'div'> {
  onClickCancel: any
}

const ActionButtons = ({ onClickCancel }: ActionButtonsProps) => {
  const { pending } = useFormStatus()

  return (
    <div className="flex w-full flex-col gap-2">
      <Button
        variant="brand"
        className="w-full justify-start gap-2"
        size="lg"
        type="submit"
        disabled={pending}
      >
        <div className="flex flex-grow items-center gap-2">
          <CheckCircleBold strokeWidth={1.2} className="size-5" />
          {pending ? 'Confirming...' : 'Confirm Payment'}
        </div>
        <KeyChip variant="lighter" shortcut="enter" />
      </Button>

      <Button
        variant="outline"
        className="w-full justify-start gap-2"
        size="lg"
        onClick={onClickCancel}
        disabled={pending}
      >
        <div className="flex flex-grow items-center gap-2">
          <CloseCircleBold strokeWidth={1.2} className="size-5" />
          Cancel Payment
        </div>
        <KeyChip shortcut="esc" />
      </Button>
    </div>
  )
}